import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Grid, TextField, MenuItem, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Paper, Typography, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Divider,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Switch, Tooltip
} from "@mui/material";
import { Refresh, ExpandMore, ContentCopy, Search, BugReport, Groups, Send, Delete, Replay, TravelExplore } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { aiCallApi, dhaka, formatNumber, isObjectId, useInterval } from "./useAICalling";
import { AICallingPage, Loading, ErrorState, Metric, SectionCard, ConfirmDialog } from "./AICallingComponents";

/**
 * Ticket agent — Claude Code investigates the support tickets the quick AI answer can't
 * settle (BibahoBanglaBackend/TicketAgent, Docs/Backend/TICKET_AGENT.md). Read-only: a bug
 * in our code is written down (why + how to fix) for the owner to fix with Claude Code.
 */

export const RUN_STATUS = {
  queued: { label: "Queued", color: "default" },
  investigating: { label: "Investigating", color: "info" },
  sending: { label: "Sending", color: "info" },
  sent: { label: "Sent", color: "success" },
  draft: { label: "Draft (held)", color: "warning" },
  no_reply: { label: "No reply needed", color: "default" },
  superseded: { label: "Superseded", color: "default" },
  discarded: { label: "Discarded", color: "default" },
  failed: { label: "Failed", color: "error" },
};
const ISSUE_STATUS = { open: { label: "Code issue", color: "error" }, fixed: { label: "Fixed", color: "success" }, dismissed: { label: "Dismissed", color: "default" } };
const MODES = { auto: "Auto-send", draft: "Draft only", off: "Off" };
const PAGE_SIZES = [25, 50, 100];
const FILTER_KEYS = ["status", "codeIssue", "needsTeam", "q"];
const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const duration = (ms) => (ms ? `${Math.round(ms / 1000)} s` : "—");

/** The note to paste into Claude Code. */
const issueMarkdown = (run) => {
  const c = run.result?.codeIssue || {};
  return [
    `Fix this BibahoBangla bug found by the ticket agent (run ${run._id}, ticket ${run.ticketId}).`,
    `Title: ${c.title} [${c.severity}]`,
    `Member reported: ${String(run.triggerExcerpt || "").replace(/\s+/g, " ").slice(0, 300)}`,
    `Why it happens: ${c.why}`,
    `How to fix: ${c.howToFix}`,
    c.files?.length ? `Files: ${c.files.join(", ")}` : "",
    `When fixed: ssh bd 'cd /home/ubuntu/EtechEcosystem/BibahoBanglaNew/BibahoBanglaBackend && node scripts/ticket-code-issues.mjs --mark ${run._id} fixed --note "<commit>"'`,
  ].filter(Boolean).join("\n");
};

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Could not copy");
  }
};

const StatusChip = ({ status }) => {
  const s = RUN_STATUS[status] || { label: status, color: "default" };
  return <Chip size="small" label={s.label} color={s.color} />;
};

/* ------------------------------------------------------------------ run detail */

const RunDialog = ({ runId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const load = useCallback(async () => {
    const res = await aiCallApi.ticketAgentRun(runId);
    if (!res.ok) return setError(res.error);
    setError("");
    setData(res.data);
    setText((t) => t || res.data?.run?.result?.reply || "");
  }, [runId]);
  useEffect(() => {
    load();
  }, [load]);
  const live = ["queued", "investigating", "sending"].includes(data?.run?.status);
  useInterval(load, 5000, live);

  const act = async (action, body, done) => {
    setBusy(action);
    const res = await aiCallApi.ticketAgentAction(runId, action, body);
    setBusy("");
    if (!res.ok) return toast.error(res.error);
    toast.success(res.message || "Done");
    onChanged();
    if (done) done();
    else load();
  };

  const run = data?.run;
  const ticket = data?.ticket;
  const r = run?.result || {};
  const issue = r.codeIssue || {};

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        {run ? run.title || "Ticket" : "Run"}
        {run && <Box sx={{ mt: 0.5, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <StatusChip status={run.status} />
          {run.isTest && <Chip size="small" variant="outlined" label="Test run" />}
          {r.confidence && <Chip size="small" variant="outlined" label={`Confidence: ${r.confidence}`} />}
          <Typography variant="caption" color="text.secondary">
            {dhaka(run.createdAt)} · {run.model || "—"}/{run.effort || "—"} · {usd(run.costUsd)} · {duration(run.durationMs)} · {run.numTurns || 0} turns
          </Typography>
        </Box>}
      </DialogTitle>
      <DialogContent dividers>
        {error && <ErrorState error={error} onRetry={load} />}
        {!run && !error && <Loading />}
        {run && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Why it came to the agent</Typography>
              <Typography variant="body2">{run.reason || "—"}</Typography>
              <Typography variant="caption" color="text.secondary">Ticket {run.ticketId}{run.memberName ? ` · ${run.memberName}` : ""}{run.userId ? ` · user ${run.userId}` : ""}</Typography>
            </Box>
            {run.error && <Alert severity="error">{run.error}</Alert>}
            {run.holdReason && <Alert severity={run.status === "draft" ? "warning" : "info"}>{run.holdReason}</Alert>}
            {live && <Alert severity="info" icon={<CircularProgress size={18} />}>The agent is working on this ticket…</Alert>}

            {r.summary && (
              <Box>
                <Typography variant="subtitle2">What it found</Typography>
                <Typography variant="body2">{r.summary}</Typography>
                {!!r.findings?.length && (
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {r.findings.map((f, i) => <li key={i}><Typography variant="body2" color="text.secondary">{f}</Typography></li>)}
                  </Box>
                )}
              </Box>
            )}

            {(run.status === "draft" || run.sentText || r.reply) && (
              <Box>
                <Typography variant="subtitle2">{run.status === "sent" ? `Reply sent by ${run.sentBy} · ${dhaka(run.sentAt)}` : "Reply"}</Typography>
                {run.status === "draft" && !run.isTest ? (
                  <TextField fullWidth multiline minRows={3} value={text} onChange={(e) => setText(e.target.value)} inputProps={{ lang: "bn" }} sx={{ mt: 1 }} />
                ) : (
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 1, whiteSpace: "pre-wrap" }} lang="bn">{run.sentText || r.reply || "—"}</Paper>
                )}
                {!!run.guard?.reasons?.length && <Typography variant="caption" color="error">Safety check: {run.guard.reasons.join("; ")}</Typography>}
              </Box>
            )}

            {r.needsTeam && (
              <Alert
                severity={run.teamHandledAt ? "success" : "warning"}
                icon={<Groups />}
                action={
                  <Button color="inherit" size="small" disabled={!!busy} onClick={() => act("teamHandled", { undo: !!run.teamHandledAt })}>
                    {run.teamHandledAt ? "Undo" : "Mark handled"}
                  </Button>
                }
              >
                <strong>Needs the team:</strong> {r.teamNote || "—"}
                {run.teamHandledAt && <div>Handled by {run.teamHandledBy} · {dhaka(run.teamHandledAt)}</div>}
              </Alert>
            )}

            {issue.found && (
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: run.codeIssueStatus === "open" ? "error.main" : "divider" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <BugReport fontSize="small" color="error" /> {issue.title} <Chip size="small" label={issue.severity} sx={{ ml: 0.5 }} />
                  </Typography>
                  <Chip size="small" label={ISSUE_STATUS[run.codeIssueStatus]?.label || run.codeIssueStatus} color={ISSUE_STATUS[run.codeIssueStatus]?.color || "default"} />
                </Box>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>Why it happens</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{issue.why}</Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>How to fix</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{issue.howToFix}</Typography>
                {!!issue.files?.length && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, fontFamily: "monospace" }}>{issue.files.join("\n")}</Typography>}
                {run.codeIssueBy && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>{run.codeIssueStatus} by {run.codeIssueBy} · {dhaka(run.codeIssueUpdatedAt)}{run.codeIssueNote ? ` · ${run.codeIssueNote}` : ""}</Typography>}
                <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                  <Button size="small" variant="contained" startIcon={<ContentCopy />} onClick={() => copy(issueMarkdown(run))}>Copy for Claude Code</Button>
                  {run.codeIssueStatus !== "fixed" && <Button size="small" color="success" disabled={!!busy} onClick={() => act("codeIssue", { status: "fixed" })}>Mark fixed</Button>}
                  {run.codeIssueStatus !== "dismissed" && <Button size="small" disabled={!!busy} onClick={() => act("codeIssue", { status: "dismissed" })}>Dismiss</Button>}
                  {run.codeIssueStatus !== "open" && <Button size="small" disabled={!!busy} onClick={() => act("codeIssue", { status: "open" })}>Reopen</Button>}
                </Box>
              </Paper>
            )}

            {ticket && (
              <Accordion disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Ticket conversation ({(ticket.messages || []).length + 1}) · {ticket.status}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 1 }} lang="bn">
                  <Typography variant="body2"><strong>Member · {dhaka(ticket.createdAt)}:</strong> {ticket.description}</Typography>
                  {(ticket.messages || []).map((m) => (
                    <Typography key={m._id} variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      <strong>{m.sender === "user" ? "Member" : m.senderName || "Support"} · {dhaka(m.createdAt)}:</strong> {m.content}
                    </Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {!!run.transcript?.length && (
              <Accordion disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">What the agent did ({run.transcript.length} steps)</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ maxHeight: 420, overflow: "auto" }}>
                  {run.transcript.map((t, i) => (
                    <Box key={i} sx={{ mb: 1, fontFamily: t.kind === "text" ? undefined : "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", color: t.kind === "error" ? "error.main" : t.kind === "result" ? "text.secondary" : "text.primary" }}>
                      <strong>{t.kind === "tool" ? `▶ ${t.name}` : t.kind}</strong> {t.text}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        {run && !live && !run.isTest && (
          <Button startIcon={<Replay />} disabled={!!busy} onClick={() => act("retry")}>Run again</Button>
        )}
        {run?.status === "draft" && !run.isTest && (
          <>
            <Button color="error" startIcon={<Delete />} disabled={!!busy} onClick={() => setConfirmDiscard(true)}>Discard</Button>
            <Button variant="contained" startIcon={busy === "send" ? <CircularProgress size={16} color="inherit" /> : <Send />} disabled={!!busy || !text.trim()} onClick={() => act("send", { text })}>
              Send to member
            </Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <ConfirmDialog open={confirmDiscard} title="Discard this draft?" confirmText="Discard" confirmColor="error" onClose={() => setConfirmDiscard(false)} onConfirm={() => act("discard")}>
        The member gets no reply from the agent. Answer the ticket yourself on the Support Tickets page.
      </ConfirmDialog>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ page */

const AICallingTicketAgent = () => {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const filters = useMemo(() => {
    const p = new URLSearchParams(query);
    return Object.fromEntries(FILTER_KEYS.map((k) => [k, p.get(k) || ""]));
  }, [query]);
  const page = Math.max(1, parseInt(new URLSearchParams(query).get("page"), 10) || 1);
  const limitParam = parseInt(new URLSearchParams(query).get("limit"), 10);
  const limit = PAGE_SIZES.includes(limitParam) ? limitParam : 25;
  const openRun = new URLSearchParams(query).get("run") || "";

  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState(filters.q);
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [ov, list] = await Promise.all([aiCallApi.ticketAgent(), aiCallApi.ticketAgentRuns({ ...filters, page, limit })]);
    setLoading(false);
    if (ov.ok) setOverview(ov.data);
    if (!list.ok) return setError(list.error);
    setError("");
    setRows(Array.isArray(list.data) ? list.data : []);
    setPagination(list.body?.pagination || { total: 0, totalPages: 1 });
  }, [filters, page, limit]);
  useEffect(() => {
    load();
  }, [load]);
  useInterval(load, 15000, rows.some((r) => ["queued", "investigating", "sending"].includes(r.status)));

  const update = (changes, keepPage = false) => {
    const next = new URLSearchParams(query);
    for (const [k, v] of Object.entries(changes)) {
      if (v === "" || v === null || v === undefined) next.delete(k);
      else next.set(k, String(v));
    }
    if (!keepPage) next.delete("page");
    setParams(next);
  };

  const investigate = async () => {
    const res = await aiCallApi.investigateTicket(ticketId.trim(), note.trim());
    if (!res.ok) {
      toast.error(res.error);
      return false;
    }
    toast.success(res.message || "Queued");
    setTicketId("");
    setNote("");
    load();
    return true;
  };

  const ov = overview;
  const s = ov?.settings || {};
  return (
    <AICallingPage
      title="Ticket agent"
      subtitle="Claude Code investigates the tickets the quick AI answer can't settle, and replies in short. It never changes code: bugs are written down for you to fix."
      actions={
        <>
          <Button variant="outlined" startIcon={<TravelExplore />} onClick={() => setInvestigateOpen(true)}>Investigate a ticket</Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
        </>
      }
    >
      {ov && (
        <SectionCard>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
            <Chip label={ov.agent.running ? "Agent running" : "Agent not running"} color={ov.agent.running ? "success" : "error"} />
            <Chip variant="outlined" label={`Mode: ${MODES[s.mode] || s.mode || "—"}`} color={s.mode === "off" ? "default" : s.mode === "draft" ? "warning" : "primary"} />
            <Chip variant="outlined" label={`${s.model || "opus"} · effort ${s.effort || "medium"} · up to $${s.maxBudgetUsd ?? 3}/ticket`} />
            <Chip variant="outlined" label={`Quick answer: ${ov.quickAnswer.enabled ? ov.quickAnswer.model : "off"}`} />
            {ov.agent.current && <Chip color="info" label={`Working on ticket ${ov.agent.current.ticketId} since ${dhaka(ov.agent.current.startedAt)}`} />}
            <Typography variant="caption" color="text.secondary">Change these in Settings → Support tickets.</Typography>
          </Box>
          <Grid container spacing={2}>
            <Metric label="Replies sent (30 days)" value={formatNumber(ov.counts.sent || 0)} md={2} />
            <Metric label="Drafts held" value={formatNumber(ov.counts.draft || 0)} color={ov.counts.draft ? "#ed6c02" : undefined} md={2} />
            <Metric label="Failed" value={formatNumber(ov.counts.failed || 0)} color={ov.counts.failed ? "#d32f2f" : undefined} md={2} />
            <Metric label="Needs the team" value={formatNumber(ov.needsTeam)} color={ov.needsTeam ? "#ed6c02" : undefined} md={2} />
            <Metric label="Open code issues" value={formatNumber(ov.openCodeIssues)} color={ov.openCodeIssues ? "#d32f2f" : undefined} md={2} />
            <Metric label="Cost (7 / 30 days)" value={`${usd(ov.cost.last7.usd)} / ${usd(ov.cost.last30.usd)}`} sub={`${ov.cost.last30.runs} runs in 30 days`} md={2} />
          </Grid>
        </SectionCard>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" placeholder="Ticket id, title or member" value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && update({ q: q.trim() })}
              InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Status" value={filters.status} onChange={(e) => update({ status: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(RUN_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Code issues" value={filters.codeIssue} onChange={(e) => update({ codeIssue: e.target.value })}>
              <MenuItem value="">All runs</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="fixed">Fixed</MenuItem>
              <MenuItem value="dismissed">Dismissed</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={8} md={3}>
            <FormControlLabel control={<Switch checked={filters.needsTeam === "yes"} onChange={(e) => update({ needsTeam: e.target.checked ? "yes" : "" })} />} label="Needs the team (not handled)" />
          </Grid>
          <Grid item xs={4} md={1}>
            <Button fullWidth variant="contained" onClick={() => update({ q: q.trim() })}>Search</Button>
          </Grid>
        </Grid>
      </Paper>

      {error && !rows.length ? (
        <ErrorState error={error} onRetry={load} />
      ) : loading && !rows.length ? (
        <Loading text="Loading runs..." />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 960, opacity: loading ? 0.6 : 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Why</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reply</TableCell>
                  <TableCell>Flags</TableCell>
                  <TableCell align="right">Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No runs yet. Most tickets are answered by the quick AI answer; only real problems come here.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r._id} hover className="aic-row-link" onClick={() => update({ run: r._id }, true)}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{dhaka(r.createdAt)}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={r.title}>{r.title || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">{r.memberName || r.ticketId}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="caption" noWrap display="block" title={r.reason}>{r.reason || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={r.status} />
                        {r.triggerMessageId?.startsWith("test-") && <Chip size="small" variant="outlined" label="Test" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" noWrap lang="bn" title={r.sentText || r.result?.reply}>{r.sentText || r.result?.reply || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {r.codeIssueStatus !== "none" && <Tooltip title={r.result?.codeIssue?.title || ""}><Chip size="small" icon={<BugReport />} label={ISSUE_STATUS[r.codeIssueStatus]?.label} color={ISSUE_STATUS[r.codeIssueStatus]?.color} /></Tooltip>}
                          {r.result?.needsTeam && <Chip size="small" icon={<Groups />} label={r.teamHandledAt ? "Team: handled" : "Needs team"} color={r.teamHandledAt ? "default" : "warning"} />}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{usd(r.costUsd)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={Math.min(page - 1, Math.max(0, (pagination.totalPages || 1) - 1))}
            onPageChange={(_, p) => update({ page: p + 1 }, true)}
            rowsPerPage={limit}
            rowsPerPageOptions={PAGE_SIZES}
            onRowsPerPageChange={(e) => update({ limit: e.target.value })}
            labelRowsPerPage="Rows"
          />
        </Paper>
      )}

      {openRun && <RunDialog runId={openRun} onClose={() => update({ run: "" }, true)} onChanged={load} />}

      <ConfirmDialog
        open={investigateOpen}
        title="Investigate a ticket"
        confirmText="Queue"
        disabled={!isObjectId(ticketId)}
        onClose={() => setInvestigateOpen(false)}
        onConfirm={investigate}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The agent investigates the ticket's latest member message and answers it (or holds a draft in draft mode). The ticket id is on the Support Tickets page.
        </Typography>
        <TextField fullWidth size="small" label="Ticket id" value={ticketId} onChange={(e) => setTicketId(e.target.value)} sx={{ mb: 2 }} error={!!ticketId && !isObjectId(ticketId)} />
        <TextField fullWidth size="small" label="Note for the agent (optional)" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} />
        <Divider sx={{ mt: 2 }} />
      </ConfirmDialog>
    </AICallingPage>
  );
};

export default AICallingTicketAgent;
